import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import PartnerBatchBooking from '../../models/partnerBatchBooking';
import { WeezHeader, InfoText, Button, Modal, Card, ContentCard, FilterSetController, Table, TableCellDropdown, Dropdown, Icon, ToastContext } from '@weezevent/nacre';
import LoadingTable from '../../components/loadingTable';
import CSS from './styles/partnerBatchBookingDetail.module.css';
import { useNotifications, Notification } from '../../components/notifications/notificationsProvider';
import { InheritedProperty } from '../rates/ratesList.js';

export default function PartnerBatchBookingDetail({ organization, partnerId, batchBookingId }) {
    const { t } = useTranslation();
    const history = useHistory();

    const [ratesSelected, setRatesSelected] = React.useState([]);
    const [showModal, setShowModal] = React.useState(false);

    const [batchBooking, loadingBatchBooking] = PartnerBatchBooking.useApiModel(
        {
            id: batchBookingId,
            organizationId: organization.id,
            partnerId: partnerId
        },
        [batchBookingId, organization, partnerId]
    );

    const handleBack = React.useCallback(() => {
        history.push(`/ticket/O${organization.id}/partners/${partnerId}/batches`);
    }, [history, organization, partnerId]);

    return (
        <>
            <WeezHeader
                backLink={{
                    onClick: () => handleBack()
                }}
                title={t('ticket.partners.batch.booking.detail.titre')}
                soutitle={t('ticket.partners.batch.booking.detail.titre')}
            >
                {batchBooking?.name}
            </WeezHeader>
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={batchBooking?.rates?.length}
                        labelTotal={t('ticket.partners.batch.booking.detail.batches', {
                            count: batchBooking?.rates?.length || 0
                        })}
                        onChange={() => {}}
                        search={{}}
                    />
                </ContentCard>
                <>
                    <Table useNew>
                        <Table.Header>
                            <Table.Tr>
                                <th>{t('ticket.partners.batch.booking.detail.rate')}</th>
                                <th>{t('ticket.partners.batch.booking.detail.inventory_event')}</th>
                                <th>{t('ticket.partners.batch.booking.detail.quota')}</th>
                                <th>{t('ticket.partners.batch.booking.detail.actions')}</th>
                            </Table.Tr>
                        </Table.Header>
                        {!loadingBatchBooking ? (
                            <>
                                {batchBooking?.rates?.length > 0 ? (
                                    <Table.TBody>
                                        <BatchBookingsTable rates={batchBooking.rates} setRatesSelected={setRatesSelected} setShowModal={setShowModal} />
                                    </Table.TBody>
                                ) : (
                                    <Table.Tr>
                                        <td colSpan={4}>
                                            <Card.EmptyState />
                                        </td>
                                    </Table.Tr>
                                )}
                            </>
                        ) : (
                            <LoadingTable loading={loadingBatchBooking} />
                        )}
                    </Table>
                </>
            </Card>
            <FreeModal
                showModal={showModal}
                onClose={() => {
                    setRatesSelected([]);
                    setShowModal(false);
                }}
                organizationId={organization.id}
                partnerId={partnerId}
                batchBooking={batchBooking}
                ratesSelected={ratesSelected}
            />
        </>
    );
}

PartnerBatchBookingDetail.propTypes = {
    organization: PropTypes.object.isRequired,
    partnerId: PropTypes.string.isRequired,
    batchBookingId: PropTypes.object.isRequired
};

export function BatchBookingsTable({ rates, setRatesSelected, setShowModal }) {
    return rates.map(rate => <TableRow key={rate.id} rate={rate} setRatesSelected={setRatesSelected} setShowModal={setShowModal} />);
}

function RateName({ rate }) {
    if (!rate) {
        return null;
    }
    return (
        <div className={CSS['icon_seat_link']}>
            {rate.category && (
                <div className={CSS['icon-seat-espace']}>
                    <Icon name={'seat'} />
                </div>
            )}
            <InheritedProperty
                displayIcon={true}
                property="name"
                object={rate}
                handleProperty={name => <div style={{ display: 'flex', alignItems: 'center' }}>{name}</div>}
                template="rate_template"
            />
        </div>
    );
}
function RateGroupName({ group }) {
    if (!group) {
        return null;
    }
    return (
        <div className={CSS['icon_seat_link']}>
            <InheritedProperty
                displayIcon={true}
                property="name"
                object={group}
                handleProperty={name => <div style={{ display: 'flex', alignItems: 'center' }}>{name}</div>}
                template="group_template"
            />
        </div>
    );
}
export function TableRow({ rate, setRatesSelected, setShowModal }) {
    const { t } = useTranslation();

    return (
        <>
            <Table.Tr>
                <Table.Td>
                    <InfoText subtitle={<RateGroupName group={rate?.group} />}>
                        <span>{<RateName rate={rate} />}</span>
                    </InfoText>
                </Table.Td>

                <Table.Td>
                    <InfoText subtitle={rate.event.name}>
                        <span>{rate.inventories[0]?.name}</span>
                    </InfoText>
                </Table.Td>

                <Table.Td>
                    <span>
                        {rate.taken}/{rate.total}
                    </span>
                </Table.Td>

                <Table.Td>
                    <TableCellDropdown>
                        <Dropdown.Item
                            item={t('ticket.partners.batch.booking.detail.liberation', { count: rate.total - rate.taken })}
                            onClick={() => {
                                setRatesSelected([rate]);
                                setShowModal(true);
                            }}
                        />
                    </TableCellDropdown>
                </Table.Td>
            </Table.Tr>
        </>
    );
}

export function FreeModal({ showModal, onClose, organizationId, partnerId, batchBooking, ratesSelected }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const { addNotification } = useNotifications();
    const [submitting, setSubmitting] = React.useState(false);

    const freeRemainBooking = React.useCallback(() => {
        if (!batchBooking) {
            return null;
        }
        batchBooking
            .freeRemainingBookings({
                organizationId: organizationId,
                partnerId: partnerId,
                rates: ratesSelected
            })
            .then(response => {
                addNotification(new Notification('async_actions', 'free_remaining_bookings', 'partners', response?.response?.task_id, 'ticket.partners.batch.booking.detail.'));
                toast.info(t('ticket.manager.attendees.action.generic.toast.async'));
                setSubmitting(false);
            })
            .catch(error => {
                setSubmitting(false);
                toast.error(t('ticket.partners.batch.booking.detail.error'));
            });
    }, [batchBooking, organizationId, partnerId, ratesSelected]);

    return (
        <Modal
            open={showModal}
            onClose={onClose}
            allowExternalClosing={false}
            title={t('ticket.partners.batch.booking.detail.free_confirm_title')}
            subtitle={t('ticket.partners.batch.booking.detail.free_confirm_description')}
            submitButton={
                <Button
                    primary
                    disabled={false}
                    isLoading={false}
                    onClick={() => {
                        setSubmitting(true);
                        freeRemainBooking();
                        onClose();
                    }}
                    label={t('ticket.partners.batch.booking.detail.CTA_confirm')}
                />
            }
            isLoading={submitting}
            cancelButton={<Button onClick={onClose} label={t('ticket.partners.batch.booking.detail.CTA_return')} />}
        />
    );
}
